"use strict";
/* globals module */
/**
 * @module calendarEventClient
 * @description  CalendarEvent Client provides an easy way to consume CalendarEvent REST API end-points. In order to obtain needed routes `calendarEventClient` uses `calendarEventRoute`.
 */
Object.defineProperty(exports, "__esModule", { value: true });
var tslib_1 = require("tslib");
var inversify_1 = require("inversify");
;
var httpApi_1 = require("../../../httpApi");
var __1 = require("../");
var CalendarEventClient = /** @class */ (function () {
    function CalendarEventClient(calendarEventBatchClient, calendarEventRoute, apiClient) {
        this.calendarEventBatchClient = calendarEventBatchClient;
        this.calendarEventRoute = calendarEventRoute;
        this.apiClient = apiClient;
    }
    Object.defineProperty(CalendarEventClient.prototype, "routeDefinition", {
        get: function () {
            return this.calendarEventRoute;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(CalendarEventClient.prototype, "batch", {
        get: function () {
            return this.calendarEventBatchClient;
        },
        enumerable: true,
        configurable: true
    });
    /**
     * Returns a promise that is resolved once the find action has been performed. Success response returns a list of CalendarEvent resources matching the given criteria.
     * @method
     * @param options Query resource GetCalendarEventOptions object.
     * @returns A promise that is resolved once the find action has been performed.
     * @example calendarEventClient.find({
                    pageNumber: 1,
                    pageSize: 10,
                    orderBy: '<field>',
                    orderDirection: '<asc|desc>',
                    search: '<search-phrase>',
                    ids: <event-identifiers>,
                    ownerIds: <event-owner-identifiers>,
                    calendarIds: <calendars-identifiers>,
                    calendarNames: <calendar-names>,
                    statusIds: <event-status-identifiers>,
                    typeIds: <event-type-identifiers>,
                    from: <start-date>,
                    to: <end-date>
                })
                .then(function (collection) {
                    // perform success action here
                },
                function (response, status, headers, config) {
                    // perform error handling here
                });
     */
    CalendarEventClient.prototype.find = function (options) {
        return this.apiClient.get(this.routeDefinition.find(options));
    };
    /**
     * Returns a promise that is resolved once the get action has been performed. Success response returns the CalendarEvent resource.
     * @method
     * @param id CalendarEvent id which uniquely identifies CalendarEvent resource that needs to be retrieved.
     * @param options Query resource GetRequstOptions object.
     * @returns A promise that is resolved once the get action has been performed.
     * @example calendarEventClient.get(id)
                .then(function (data) {
                    // perform success action here
                },
                function (response, status, headers, config) {
                    // perform error handling here
                });
     */
    CalendarEventClient.prototype.get = function (id, options) {
        return this.apiClient.get(this.routeDefinition.get(id, options));
    };
    /**
     * Returns a promise that is resolved once the getByEmailOrFullName action has been performed. Success response returns the CalendarEvent resource
     * @method
     * @param id CalendarEvent id which uniquely identifies CalendarEvent resource.
     * @param emailOrFullName Email or full name.
     * @param securityToken Security Token.
     * @param options Query resource options object
     * @example calendarEventClient.get(id, email@example.com)
                .then(function (data) {
                    // perform success action here
                },
                function (response, status, headers, config) {
                    // perform error handling here
                });
     */
    CalendarEventClient.prototype.getByEmailOrFullName = function (id, emailOrFullName, securityToken, options) {
        var headers;
        headers['securityToken'] = securityToken;
        return this.apiClient.get(this.routeDefinition.getByEmailOrFullName(id, emailOrFullName, options), headers);
    };
    /**
     * Returns a promise that is resolved once the create CalendarEvent action has been performed. This action creates a new CalendarEvent resource.
     * @method
     * @param data A CalendarEvent object that needs to be inserted into the system.
     * @returns A promise that is resolved once the create CalendarEvent action has been performed.
     * @example calendarEventClient.create({
                    author: <user-info>,
                    authorId: '<author-id>',
                    calendar: <calendar>,
                    calendarId: '<calendar-id>',
                    description: '<description>',
                    detail: <calendar-event-detail>,
                    endTime: '<end-time>',
                    isAllDay: <true|false>,
                    isRecurring: <true|false>,
                    json: '<json>',
                    startTime: '<start-time>',
                    title: '<title'>
                })
                .then(function (data) {
                    // perform success action here
                },
                function (response, status, headers, config) {
                    // perform error handling here
                });
     */
    CalendarEventClient.prototype.create = function (data) {
        return this.apiClient.post(this.routeDefinition.create(data), this.routeDefinition.createParams(data));
    };
    /**
     * Returns a promise that is resolved once the update CalendarEvent action has been performed. This action updates a CalendarEvent resource. This route uses HAL enabled objects to obtain routes and therefore it doesn't apply `calendarEventRoute` route template. Here is an example of how a route can be obtained from HAL enabled objects:
     * ```
     * let params = modelMapper.removeParams(calendarEvent);
     * let uri = params['model'].links('put').href;
     * ```
     * @method
     * @param data A Calendar Event  object used to update specified CalendarEvent resource.
     * @returns A promise that is resolved once the update CalendarEvent action has been performed.
     * @example calendarEvent is a resource previously fetched using get action.
                calendarEvent.title = '<title>';
                calendarEventClient.update(calendarEvent)
                .then(function (data) {
                    // perform success action here
                },
                function (response, status, headers, config) {
                    // perform error handling here
                });
     */
    CalendarEventClient.prototype.update = function (data) {
        return this.apiClient.put(this.routeDefinition.update(data), this.routeDefinition.updateParams(data));
    };
    /**
     * Returns a promise that is resolved once the remove action has been performed. This action will remove a CalendarEvent resource from the system if successfully completed. This route uses HAL enabled objects to obtain routes and therefore it doesn't apply `calendarEventRoute` route template. Here is an example of how a route can be obtained from HAL enabled objects:
     * ```
     * let params = modelMapper.removeParams(calendarEvent);
     * let uri = params['model'].links('delete').href;
     * ```
     * @method
     * @param data An calendarEvent object used to delete specified CalendarEvent resource.
     * @returns A promise that is resolved once the remove action has been performed.
     * @example calendarEvent is a resource previously fetched using get action.
                calendarEventClient.remove(calendarEvent)
                .then(function (data) {
                    // perform success action here
                },
                function (response, status, headers, config) {
                    // perform error handling here
                });
     */
    CalendarEventClient.prototype.remove = function (data) {
        return this.apiClient.delete(this.routeDefinition.delete(data));
    };
    /**
     * Returns a promise that is resolved once the purge action has been performed. This action will remove all CalendarEvent resources from the system if succesfully completed.
     * @method
     * @param calendarId Calendar identifier which uniquely identifies a calendar resource which will have its Events purged.
     * @returns A promise that is resolved once the purge action has been performed.
     * @example calendarEventClient.purge()
                .then(function (data) {
                    // perform success action here
                },
                function (response, status, headers, config) {
                    // perform error handling here
                });
     */
    CalendarEventClient.prototype.purge = function (calendarId) {
        return this.apiClient.delete(this.routeDefinition.purge(calendarId));
    };
    var _a, _b, _c;
    CalendarEventClient = tslib_1.__decorate([
        inversify_1.injectable(),
        tslib_1.__param(0, inversify_1.inject(__1.TYPES.CalendarEventBatchClient)),
        tslib_1.__param(1, inversify_1.inject(__1.TYPES.CalendarEventRoute)),
        tslib_1.__param(2, inversify_1.inject(httpApi_1.httpTYPES.ApiClient)),
        tslib_1.__metadata("design:paramtypes", [typeof (_a = typeof __1.CalendarEventBatchClient !== "undefined" && __1.CalendarEventBatchClient) === "function" ? _a : Object, typeof (_b = typeof __1.CalendarEventRoute !== "undefined" && __1.CalendarEventRoute) === "function" ? _b : Object, typeof (_c = typeof httpApi_1.ApiClient !== "undefined" && httpApi_1.ApiClient) === "function" ? _c : Object])
    ], CalendarEventClient);
    return CalendarEventClient;
}());
exports.CalendarEventClient = CalendarEventClient;
/**
 * @copyright (c) 2017 Mono Ltd
 * @license MIT
 * @author Mono Ltd
 * @overview
 ***Notes:**
 - Refer to the [Baasic REST API](http://dev.baasic.com/api/reference/home) for detailed information about available Baasic REST API end-points.
 - All end-point objects are transformed by the associated route definition.
 */ 
